"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { useParams, useLocation, useHistory, Link } from "react-router-dom";
import {
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tabs,
  Tab,
  Box,
  Grid,
  Paper,
  Avatar,
  CircularProgress,
  AppBar,
  Toolbar,
  Chip,
  IconButton,
  Breadcrumbs,
  Container,
  alpha,
  useTheme,
  Stack,
  Fade,
} from "@mui/material";
import {
  ArrowBack,
  GetApp,
  Description,
  CalendarToday,
  AccessTime,
  Home,
  Folder,
  Person,
  Work,
  Business,
  Schedule,
  Warning,
  Download,
  Share,
  Print,
} from "@mui/icons-material";
import { requestID, getUser } from "../../../common/utils";
import { getDisciplinaryCase } from "../../../api/disciplinaryService";
import DisciplinaryActionForm from "../../../components/disciplinary/DisciplinaryActionForm";
import DisciplinaryResponseForm from "../../../components/disciplinary/DisciplinaryResponseForm";
import DisciplinaryStatusBadge from "../../../components/disciplinary/DisciplinaryStatusBadge";
import DisciplinaryTimeline from "../../../components/disciplinary/DisciplinaryTimeline";
import { getAllEmployees } from "../../saas_sales_app/outstanding/OutstandingAnalysisService";
import { IonContent, IonPage } from "@ionic/react";

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <Fade in={value === index} timeout={300}>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`case-detail-tabpanel-${index}`}
        aria-labelledby={`case-detail-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
    </Fade>
  );
}

const DisciplinaryCaseDetailPage: React.FC = () => {
  const theme = useTheme();
  const uselocation = useLocation();
  const queryParams = new URLSearchParams(uselocation.search);
  const id = queryParams.get("id");
  const item = uselocation.state?.["item"];
  const itemId = item?.id || id || requestID();
  const history = useHistory();
  const [disciplinaryCase, setDisciplinaryCase] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [loadingManager, setLoadingManager] = useState(false);
  const [manager, setManager] = useState<any[]>([]);
  const userId = getUser()?.userData?.user_id;

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingManager(true);
      try {
        const employeeData = await getAllEmployees();
        setManager(employeeData);
      } catch (err) {
        console.error("Failed to fetch employees:", err);
        setError("Failed to load employee data. Please try again later.");
      } finally {
        setLoadingManager(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchDisciplinaryCase = async () => {
      try {
        if (id) {
          const data = await getDisciplinaryCase(userId);
          setDisciplinaryCase(data);
        }
      } catch (err) {
        setError("Failed to load disciplinary case details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDisciplinaryCase();
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    history.push("/folder/my-disciplinary/View Disciplinary");
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="70vh"
        sx={{ 
          bgcolor: 'background.default',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
        }}
      >
        <CircularProgress 
          size={60} 
          thickness={4}
          sx={{ 
            color: 'primary.main',
            mb: 3
          }} 
        />
        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
          Loading case details...
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Please wait while we fetch the information
        </Typography>
      </Box>
    );
  }

  if (error || !disciplinaryCase) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box textAlign="center" sx={{ py: 8 }}>
          <Warning sx={{ fontSize: 64, color: 'error.main', mb: 2, opacity: 0.8 }} />
          <Typography variant="h5" color="error" gutterBottom sx={{ fontWeight: 600 }}>
            {error || "Disciplinary case not found"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We couldn't retrieve the disciplinary case details you're looking for.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ 
              mt: 2,
              borderRadius: 2,
              px: 3,
              py: 1
            }}
          >
            Back to Disciplinary Management
          </Button>
        </Box>
      </Container>
    );
  }

  const isOverdue =
    item?.status === "pending" && new Date(item?.responseDate) <= new Date();
  const canRespond = item?.status === "pending" && userId !== item?.issued_by;
  const canTakeAction = item?.status === "response_submitted" || isOverdue;

  return (
    <IonPage className="pageDown">
      <IonContent>
        <Box sx={{ 
          bgcolor: 'background.default', 
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${theme.palette.background.default} 100%)`
        }}>
          {/* Modern Header */}
          <AppBar 
            position="sticky"
            elevation={1}
            sx={{ 
              bgcolor: 'background.paper',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
              backdropFilter: 'blur(20px)',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Toolbar sx={{ py: 2 }}>
              <IconButton
                edge="start"
                onClick={handleBack}
                sx={{ 
                  mr: 2, 
                  color: 'text.primary',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1)
                  }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Box sx={{ flexGrow: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{
                    width: 4,
                    height: 32,
                    bgcolor: 'primary.main',
                    borderRadius: 1
                  }} />
                  <Box>
                    <Typography variant="h4" component="h1" sx={{ 
                      color: 'text.primary', 
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(theme.palette.text.primary, 0.8)} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      Case #{item?.caseNumber || item?.id}
                    </Typography>
                    <Breadcrumbs sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary', 
                        display: 'flex', 
                        alignItems: 'center',
                        fontWeight: 500
                      }}>
                        <Home sx={{ fontSize: 16, mr: 0.5 }} />
                        Home
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary', 
                        display: 'flex', 
                        alignItems: 'center',
                        fontWeight: 500
                      }}>
                        <Folder sx={{ fontSize: 16, mr: 0.5 }} />
                        Disciplinary
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: 'primary.main', 
                        display: 'flex', 
                        alignItems: 'center',
                        fontWeight: 600
                      }}>
                        <Description sx={{ fontSize: 16, mr: 0.5 }} />
                        Case Details
                      </Typography>
                    </Breadcrumbs>
                  </Box>
                </Stack>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <DisciplinaryStatusBadge status={item?.status} />
                <Button
                  variant="outlined"
                  startIcon={<Print />}
                  sx={{ borderRadius: 2 }}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Share />}
                  sx={{ borderRadius: 2 }}
                >
                  Share
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>

          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Grid container spacing={3}>
              {/* Main Content */}
              <Grid item xs={12} lg={8}>
                <Card 
                  elevation={0}
                  sx={{ 
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    bgcolor: 'background.paper',
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.05)}`
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      sx={{
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        px: 3,
                        '& .MuiTab-root': {
                          color: 'text.secondary',
                          fontWeight: 500,
                          textTransform: 'none',
                          fontSize: '0.9rem',
                          minHeight: 56,
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                        '& .Mui-selected': {
                          color: 'primary.main',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: 'primary.main',
                          height: 3,
                          borderRadius: 2
                        },
                      }}
                    >
                      <Tab icon={<Description sx={{ fontSize: 20 }} />} iconPosition="start" label="Case Details" />
                      <Tab icon={<Schedule sx={{ fontSize: 20 }} />} iconPosition="start" label="Timeline" />
                      {canRespond && <Tab icon={<Person sx={{ fontSize: 20 }} />} iconPosition="start" label="Respond" />}
                      {canTakeAction && <Tab icon={<Warning sx={{ fontSize: 20 }} />} iconPosition="start" label="Take Action" />}
                    </Tabs>

                    <Box sx={{ p: 4 }}>
                      <TabPanel value={tabValue} index={0}>
                        {/* Case Details Content */}
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                            <Box>
                              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                                Issue Details
                              </Typography>
                              <Chip 
                                icon={<CalendarToday sx={{ fontSize: 16 }} />}
                                label={`Issued ${new Date(item?.issueDate || item?.date_created)?.toLocaleDateString()}`}
                                variant="outlined"
                                size="small"
                                sx={{ borderRadius: 1 }}
                              />
                            </Box>
                            <Avatar sx={{ 
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              width: 48,
                              height: 48
                            }}>
                              <Work />
                            </Avatar>
                          </Stack>

                          <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ 
                                p: 2.5, 
                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                borderRadius: 2
                              }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  Employee
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {item?.employeeName}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ 
                                p: 2.5, 
                                bgcolor: alpha(theme.palette.secondary.main, 0.02),
                                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                                borderRadius: 2
                              }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  Department
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {item?.department}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ 
                                p: 2.5, 
                                bgcolor: alpha(theme.palette.info.main, 0.02),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                                borderRadius: 2
                              }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  Issuing Manager
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {item?.issuedByName}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ 
                                p: 2.5, 
                                bgcolor: alpha(theme.palette.warning.main, 0.02),
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                                borderRadius: 2
                              }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  Issue Type
                                </Typography>
                                <Chip 
                                  label={item?.issueType || item?.issue_type} 
                                  color="warning"
                                  variant="filled"
                                  sx={{ fontWeight: 600, borderRadius: 1 }}
                                />
                              </Paper>
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 4, borderColor: alpha(theme.palette.divider, 0.1) }} />

                          <Box>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                              Issue Description
                            </Typography>
                            <Paper 
                              sx={{ 
                                p: 3, 
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                borderRadius: 2,
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              <Typography variant="body1" sx={{ 
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.7,
                                color: 'text.primary'
                              }}>
                                {item?.description || item?.responseText || "No description provided."}
                              </Typography>
                            </Paper>
                          </Box>

                          <Grid container spacing={3} sx={{ mt: 3 }}>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ 
                                p: 2.5, 
                                bgcolor: alpha(theme.palette.success.main, 0.02),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                                borderRadius: 2
                              }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Avatar sx={{ 
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    color: 'success.main',
                                    width: 40,
                                    height: 40
                                  }}>
                                    <CalendarToday />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      Issue Date
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      {new Date(item?.issueDate || item?.date_created).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ 
                                p: 2.5, 
                                bgcolor: isOverdue ? 
                                  alpha(theme.palette.error.main, 0.05) : 
                                  alpha(theme.palette.info.main, 0.02),
                                border: `1px solid ${isOverdue ? 
                                  alpha(theme.palette.error.main, 0.2) : 
                                  alpha(theme.palette.info.main, 0.1)}`,
                                borderRadius: 2
                              }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Avatar sx={{ 
                                    bgcolor: isOverdue ? 
                                      alpha(theme.palette.error.main, 0.1) : 
                                      alpha(theme.palette.info.main, 0.1),
                                    color: isOverdue ? 'error.main' : 'info.main',
                                    width: 40,
                                    height: 40
                                  }}>
                                    <AccessTime />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      Response Deadline
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                      fontWeight: 600,
                                      color: isOverdue ? 'error.main' : 'text.primary'
                                    }}>
                                      {new Date(item?.responseDate).toLocaleDateString()}
                                      {isOverdue && " • Overdue"}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Box>
                      </TabPanel>

                      <TabPanel value={tabValue} index={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                          <Box>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                              Case Timeline
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              History of events for this disciplinary case
                            </Typography>
                          </Box>
                          <Avatar sx={{ 
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                            width: 48,
                            height: 48
                          }}>
                            <Schedule />
                          </Avatar>
                        </Stack>
                        <DisciplinaryTimeline events={[]} />
                      </TabPanel>

                      {canRespond && (
                        <TabPanel value={tabValue} index={2}>
                          <DisciplinaryResponseForm
                            caseId={item?.caseNumber || ""}
                            disciplinaryId={item?.disciplinaryID}
                            employeeId={item?.employeeId}
                            employeeName={item?.employeeName}
                          />
                        </TabPanel>
                      )}

                      {canTakeAction && (
                        <TabPanel value={tabValue} index={2}>
                          <DisciplinaryActionForm
                            caseId={item?.discipline_id || ""}
                            employeeId={item?.employeeId || item?.user_id}
                            employeeName={item?.employeeName}
                            hasResponse={
                              item?.status === "response_submitted" ||
                              item?.status === "pending"
                            }
                          />
                        </TabPanel>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Sidebar */}
              <Grid item xs={12} lg={4}>
                {item?.isManager && (
                  <Card 
                    elevation={0}
                    sx={{ 
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      bgcolor: 'background.paper',
                      background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.05)}`
                    }}
                  >
                    <CardHeader
                      title={
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Employee Information
                        </Typography>
                      }
                      sx={{ 
                        pb: 2,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                      }}
                    />
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={3} mb={3}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main',
                            width: 64,
                            height: 64,
                            fontSize: '1.5rem',
                            fontWeight: 600
                          }}
                        >
                          {item?.employeeName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {item?.employeeName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {item?.employeeId}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 3, borderColor: alpha(theme.palette.divider, 0.1) }} />

                      <Stack spacing={2.5}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Department
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Business sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {item?.department}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Position
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Work sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              Software Engineer
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>

                      <Divider sx={{ my: 3, borderColor: alpha(theme.palette.divider, 0.1) }} />

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Disciplinary History
                        </Typography>
                        <Paper 
                          sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)'
                          }}
                        >
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {Math.floor(Math.random() * 3)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            previous cases in the last 12 months
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            component={Link}
                            to={`/employee/${item?.employeeId}/disciplinary`}
                            startIcon={<Person />}
                            sx={{ 
                              mt: 1,
                              borderRadius: 2
                            }}
                          >
                            View Full History
                          </Button>
                        </Paper>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            </Grid>
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default DisciplinaryCaseDetailPage;